import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import auditEventsService from "../../../services/system/auditEventsService";
import "./AuditEventsPage.css";

const DEFAULT_FILTERS = {
  dataAreaId: "",
  originSystem: "",
  eventType: "",
  operation: "",
  entityName: "",
  businessKey: "",
  externalKey: "",
  realUserId: "",
  correlationId: "",
  status: "",
  from: "",
  to: "",
};

const statusOptions = ["", "SUCCESS", "ERROR", "WARNING", "INFO"];
const originOptions = ["", "IDAX", "AX4", "D365FO", "EMAIL", "IA", "KAFKA", "JOB", "API", "USER"];
const operationOptions = ["", "CREATE", "UPDATE", "DELETE", "SYNC", "SEND", "RECEIVE", "PROCESS", "VALIDATE", "ERROR"];

const AuditEventsPage = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [result, setResult] = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  const rows = useMemo(() => result?.content || [], [result]);
  const totalPages = result?.totalPages || 0;

  const loadEvents = async (nextPage = page) => {
    setLoading(true);
    setError("");
    try {
      const data = await auditEventsService.search(filters, nextPage, pageSize);
      setResult(data || { content: [], totalElements: 0, totalPages: 0 });
      setPage(nextPage);
    } catch (err) {
      setError(err?.message || t("common.errorOccurred"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const submitFilters = (event) => {
    event.preventDefault();
    loadEvents(0);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
    setTimeout(() => loadEvents(0), 0);
  };

  const summary = useMemo(() => {
    const counts = { SUCCESS: 0, ERROR: 0, WARNING: 0, INFO: 0 };
    rows.forEach((row) => {
      if (counts[row.status] !== undefined) counts[row.status] += 1;
    });
    return counts;
  }, [rows]);

  return (
    <div className="audit-events-page">
      <header className="audit-page-header">
        <div>
          <h1>{t("auditEvents.title")}</h1>
          <p>{t("auditEvents.subtitle")}</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => loadEvents(page)} disabled={loading}>
          <i className="fa fa-refresh" aria-hidden="true"></i>
          <span>{t("auditEvents.refresh")}</span>
        </button>
      </header>

      <section className="audit-summary-strip" aria-label={t("auditEvents.summary")}>
        {Object.entries(summary).map(([status, count]) => (
          <div key={status} className={`audit-summary-item audit-status-${status.toLowerCase()}`}>
            <span>{t(`auditEvents.status.${status}`)}</span>
            <strong>{count}</strong>
          </div>
        ))}
      </section>

      <form className="audit-filter-panel" onSubmit={submitFilters}>
        <div className="audit-panel-title">
          <i className="fa fa-filter" aria-hidden="true"></i>
          <h2>{t("auditEvents.filters")}</h2>
        </div>
        <div className="audit-form-grid">
          <TextInput label={t("auditEvents.fields.dataAreaId")} value={filters.dataAreaId} onChange={(v) => setFilter("dataAreaId", v)} />
          <SelectInput label={t("auditEvents.fields.originSystem")} value={filters.originSystem} options={originOptions} onChange={(v) => setFilter("originSystem", v)} />
          <TextInput label={t("auditEvents.fields.eventType")} value={filters.eventType} onChange={(v) => setFilter("eventType", v)} />
          <SelectInput label={t("auditEvents.fields.operation")} value={filters.operation} options={operationOptions} onChange={(v) => setFilter("operation", v)} />
          <TextInput label={t("auditEvents.fields.entityName")} value={filters.entityName} onChange={(v) => setFilter("entityName", v)} />
          <TextInput label={t("auditEvents.fields.businessKey")} value={filters.businessKey} onChange={(v) => setFilter("businessKey", v)} />
          <TextInput label={t("auditEvents.fields.externalKey")} value={filters.externalKey} onChange={(v) => setFilter("externalKey", v)} />
          <TextInput label={t("auditEvents.fields.realUserId")} value={filters.realUserId} onChange={(v) => setFilter("realUserId", v)} />
          <TextInput label={t("auditEvents.fields.correlationId")} value={filters.correlationId} onChange={(v) => setFilter("correlationId", v)} />
          <SelectInput label={t("auditEvents.fields.status")} value={filters.status} options={statusOptions} onChange={(v) => setFilter("status", v)} />
          <DateInput label={t("auditEvents.fields.from")} value={filters.from} onChange={(v) => setFilter("from", v)} />
          <DateInput label={t("auditEvents.fields.to")} value={filters.to} onChange={(v) => setFilter("to", v)} />
          <SelectInput label={t("auditEvents.pageSize")} value={pageSize} options={[25, 50, 100, 200]} onChange={(v) => setPageSize(Number(v))} />
        </div>
        <div className="audit-actions">
          <button className="btn btn-primary" type="submit" disabled={loading}>
            <i className="fa fa-search" aria-hidden="true"></i>
            <span>{t("common.search")}</span>
          </button>
          <button className="btn btn-default" type="button" onClick={resetFilters}>
            <i className="fa fa-eraser" aria-hidden="true"></i>
            <span>{t("auditEvents.clear")}</span>
          </button>
        </div>
      </form>

      {error && <div className="audit-alert">{error}</div>}

      <section className="audit-results">
        <div className="audit-results-header">
          <h2>{t("auditEvents.events")}</h2>
          <span>{t("auditEvents.total", { count: result?.totalElements || 0 })}</span>
        </div>
        <div className="audit-table-wrap">
          <table className="audit-table">
            <thead>
              <tr>
                <th>{t("auditEvents.fields.eventTime")}</th>
                <th>{t("auditEvents.fields.status")}</th>
                <th>{t("auditEvents.fields.eventType")}</th>
                <th>{t("auditEvents.fields.entityName")}</th>
                <th>{t("auditEvents.fields.businessKey")}</th>
                <th>{t("auditEvents.fields.realUserName")}</th>
                <th>{t("auditEvents.fields.correlationId")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="audit-empty">{t("common.loading")}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="7" className="audit-empty">{t("common.noData")}</td></tr>
              ) : rows.map((row) => (
                <tr key={row.id} onClick={() => setSelectedEvent(row)}>
                  <td>{formatDate(row.eventTime)}</td>
                  <td><span className={`audit-status-pill audit-status-${String(row.status || "info").toLowerCase()}`}>{row.status}</span></td>
                  <td>{row.eventType}</td>
                  <td>{row.entityName}</td>
                  <td>{row.businessKey}</td>
                  <td>{row.realUserName || row.realUserId}</td>
                  <td className="audit-mono">{row.correlationId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="audit-pagination">
          <button className="btn btn-default" type="button" disabled={page <= 0 || loading} onClick={() => loadEvents(page - 1)}>
            <i className="fa fa-chevron-left" aria-hidden="true"></i>
          </button>
          <span>{t("common.pagination.page")} {page + 1} / {Math.max(totalPages, 1)}</span>
          <button className="btn btn-default" type="button" disabled={page + 1 >= totalPages || loading} onClick={() => loadEvents(page + 1)}>
            <i className="fa fa-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </section>

      {selectedEvent && (
        <div className="audit-modal-backdrop" onClick={() => setSelectedEvent(null)}>
          <div className="audit-modal" onClick={(event) => event.stopPropagation()}>
            <div className="audit-modal-header">
              <h2>{selectedEvent.eventType}</h2>
              <button type="button" onClick={() => setSelectedEvent(null)} aria-label={t("common.close")}>
                <i className="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <div className="audit-detail-grid">
              <Detail label={t("auditEvents.fields.eventTime")} value={formatDate(selectedEvent.eventTime)} />
              <Detail label={t("auditEvents.fields.originSystem")} value={selectedEvent.originSystem} />
              <Detail label={t("auditEvents.fields.operation")} value={selectedEvent.operation} />
              <Detail label={t("auditEvents.fields.status")} value={selectedEvent.status} />
              <Detail label={t("auditEvents.fields.entityName")} value={selectedEvent.entityName} />
              <Detail label={t("auditEvents.fields.entityId")} value={selectedEvent.entityId} />
              <Detail label={t("auditEvents.fields.businessKey")} value={selectedEvent.businessKey} />
              <Detail label={t("auditEvents.fields.externalKey")} value={selectedEvent.externalKey} />
              <Detail label={t("auditEvents.fields.realUserName")} value={selectedEvent.realUserName} />
              <Detail label={t("auditEvents.fields.technicalUser")} value={selectedEvent.technicalUser} />
              <Detail label={t("auditEvents.fields.requestId")} value={selectedEvent.requestId} />
              <Detail label={t("auditEvents.fields.correlationId")} value={selectedEvent.correlationId} />
            </div>
            {selectedEvent.errorMessage && <div className="audit-error-box">{selectedEvent.errorMessage}</div>}
            <JsonBlocks event={selectedEvent} t={t} />
          </div>
        </div>
      )}
    </div>
  );
};

const TextInput = ({ label, value, onChange, required = false }) => (
  <label>
    <span>{label}</span>
    <input className="form-control" required={required} type="text" value={value || ""} onChange={(event) => onChange(event.target.value)} />
  </label>
);

const DateInput = ({ label, value, onChange }) => (
  <label>
    <span>{label}</span>
    <input className="form-control" type="datetime-local" value={value || ""} onChange={(event) => onChange(event.target.value)} />
  </label>
);

const SelectInput = ({ label, value, options, onChange }) => (
  <label>
    <span>{label}</span>
    <select className="form-control" value={value || ""} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => (
        <option key={option} value={option}>{option || "-"}</option>
      ))}
    </select>
  </label>
);

const Detail = ({ label, value }) => (
  <div className="audit-detail">
    <span>{label}</span>
    <strong>{value || "-"}</strong>
  </div>
);

const JsonBlocks = ({ event, t }) => {
  const blocks = [
    ["beforeJson", t("auditEvents.fields.beforeJson")],
    ["afterJson", t("auditEvents.fields.afterJson")],
    ["payloadJson", t("auditEvents.fields.payloadJson")],
    ["metadataJson", t("auditEvents.fields.metadataJson")],
  ];
  return (
    <div className="audit-json-grid">
      {blocks.map(([key, label]) => (
        <div key={key}>
          <h3>{label}</h3>
          <pre>{prettyJson(event[key])}</pre>
        </div>
      ))}
    </div>
  );
};

const prettyJson = (value) => {
  if (!value) return "-";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (error) {
    return value;
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

export default AuditEventsPage;
